import { Alert, AlertDescription } from "@repo/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui/alert-dialog";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/ui/card";
import { Checkbox } from "@repo/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@repo/ui/dialog";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@repo/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/tabs";
import { Textarea } from "@repo/ui/textarea";
import { AlertTriangle, Bot, Database, Loader2, Search, Settings, ShieldAlert, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { JevCheckCell } from "@/components/poisoned-data/JevCheckCell";
import { RecentJevChecks } from "@/components/poisoned-data/RecentJevChecks";
import { JEV_BULK_MAX, useJevChecks } from "@/components/poisoned-data/useJevChecks";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import type {
  OpenRouterModelOption,
  PoisonedDataAiSetting,
  PoisonedDataMethod,
  PoisonedDataPreviewRow,
  PoisonedDataSavedSearch,
} from "@/lib/api";
import {
  deleteCurrentPoisonedDataQuery,
  deletePoisonedDataSavedSearch,
  deleteSelectedPoisonedData,
  fetchOpenRouterModels,
  fetchPoisonedDataMethods,
  fetchPoisonedDataSavedSearches,
  fetchPoisonedDataSettings,
  previewPoisonedData,
  quarantinePoisonedData,
  saveCurrentPoisonedDataSearch,
  suggestPoisonedDataFilter,
  updatePoisonedDataSettings,
} from "@/lib/api";
import {
  getTargetLabel,
  normalizePoisonedDataParameters,
  POISONED_DATA_TARGETS,
  type PoisonedDataParameters,
  type PoisonedDataTargetTable,
} from "@/lib/poisonedData/filters";

const PAGE_SIZE = 50;
const KNOB_METHODS = new Set(["price_outlier_snapshots", "implausible_sale_price", "custom_filter_builder", "ai_filter_suggestion"]);

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "number") return value.toLocaleString();
  return String(value);
}

function categoryLabel(value: PoisonedDataMethod["category"]): string {
  return value.replace("_", " ");
}

function getSettingValue(settings: PoisonedDataAiSetting[], key: string): Record<string, unknown> {
  return settings.find((setting) => setting.key === key)?.value ?? {};
}

function normalizeModelOptions(models: OpenRouterModelOption[] | string[], fallbackModel: string): OpenRouterModelOption[] {
  const normalized = models.map((model) =>
    typeof model === "string" ? { id: model, name: model } : model,
  );
  const deduped = new Map<string, OpenRouterModelOption>();
  for (const model of normalized) {
    if (model.id) deduped.set(model.id, model);
  }
  if (deduped.size === 0) {
    deduped.set(fallbackModel, { id: fallbackModel, name: fallbackModel });
  }
  return [...deduped.values()];
}

const PoisonedData = () => {
  const { toast } = useToast();
  const { canManageJobs } = useAuth();
  const [methods, setMethods] = useState<PoisonedDataMethod[]>([]);
  const [selectedMethodSlug, setSelectedMethodSlug] = useState("domain_title_pollution_snapshots");
  const [parameters, setParameters] = useState<PoisonedDataParameters>(
    normalizePoisonedDataParameters("domain_title_pollution_snapshots"),
  );
  const [rows, setRows] = useState<PoisonedDataPreviewRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchId, setBatchId] = useState<string | null>(null);
  const [matchCount, setMatchCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [deleteMode, setDeleteMode] = useState<"selected" | "all" | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [selectedRow, setSelectedRow] = useState<PoisonedDataPreviewRow | null>(null);
  const [aiGoal, setAiGoal] = useState("Find likely poisoned property scrape records with minimal false positives.");
  const [aiSuggestion, setAiSuggestion] = useState<Record<string, unknown> | null>(null);
  const [settings, setSettings] = useState<PoisonedDataAiSetting[]>([]);
  const [models, setModels] = useState<OpenRouterModelOption[]>([]);
  const [settingsDraft, setSettingsDraft] = useState<Record<string, string>>({});
  const [savedSearches, setSavedSearches] = useState<PoisonedDataSavedSearch[]>([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDraftName, setSaveDraftName] = useState("");
  const [saveDraftDescription, setSaveDraftDescription] = useState("");

  const selectedMethod = methods.find((method) => method.slug === selectedMethodSlug);
  const targetTable = parameters.targetTable ?? selectedMethod?.target_table ?? "property_snapshots";
  const allVisibleSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.targetId));
  const currentModel = String(getSettingValue(settings, "openrouter").model ?? "openai/gpt-5.5-pro");
  const canDeleteAll = Boolean(batchId && matchCount > 0 && confirmText === `DELETE ${matchCount}`);
  // Jev scores snapshots only, so its column and actions apply to property_snapshots rows.
  const snapshotIds = useMemo(
    () => rows.filter((row) => row.targetTable === "property_snapshots").map((row) => row.targetId),
    [rows],
  );
  const showJev = snapshotIds.length > 0;
  const jev = useJevChecks(snapshotIds);
  const selectedSnapshotIds = snapshotIds.filter((id) => selectedIds.has(id));
  const jevBulkIds = (selectedSnapshotIds.length > 0 ? selectedSnapshotIds : snapshotIds).slice(0, JEV_BULK_MAX);

  const loadSetup = useCallback(async () => {
    try {
      const [nextMethods, nextSettings, modelResponse, nextSavedSearches] = await Promise.all([
        fetchPoisonedDataMethods(),
        fetchPoisonedDataSettings(),
        fetchOpenRouterModels(),
        fetchPoisonedDataSavedSearches().catch(() => [] as PoisonedDataSavedSearch[]),
      ]);
      setMethods(nextMethods);
      setSettings(nextSettings);
      setModels(modelResponse.models ?? []);
      setSavedSearches(nextSavedSearches);
      setSettingsDraft(
        Object.fromEntries(nextSettings.map((setting) => [setting.key, JSON.stringify(setting.value, null, 2)])),
      );
      if (modelResponse.warning) {
        toast({ title: "OpenRouter", description: modelResponse.warning });
      }
    } catch (err) {
      toast({
        title: "Unable to load poisoned data setup",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    }
  }, [toast]);

  const handleLoadSavedSearch = useCallback(
    (saved: PoisonedDataSavedSearch) => {
      setSelectedMethodSlug(saved.method_slug);
      setParameters(
        normalizePoisonedDataParameters(saved.method_slug, {
          ...(saved.parameters ?? {}),
          targetTable: saved.target_table,
        }),
      );
      toast({
        title: "Saved search loaded",
        description: `${saved.name} — click Preview to run.`,
      });
    },
    [toast],
  );

  const handleSaveCurrentSearch = useCallback(async () => {
    const name = saveDraftName.trim();
    if (!name) return;
    try {
      const saved = await saveCurrentPoisonedDataSearch({
        name,
        description: saveDraftDescription.trim() || undefined,
        targetTable: targetTable as PoisonedDataTargetTable,
        methodSlug: selectedMethodSlug,
        parameters: normalizePoisonedDataParameters(selectedMethodSlug, parameters),
      });
      setSavedSearches((prev) => {
        const filtered = prev.filter((s) => s.id !== saved.id);
        return [saved, ...filtered];
      });
      setSaveDialogOpen(false);
      setSaveDraftName("");
      setSaveDraftDescription("");
      toast({ title: "Search saved", description: name });
    } catch (err) {
      toast({
        title: "Save failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    }
  }, [parameters, saveDraftDescription, saveDraftName, selectedMethodSlug, targetTable, toast]);

  const handleDeleteSavedSearch = useCallback(
    async (id: string) => {
      try {
        await deletePoisonedDataSavedSearch(id);
        setSavedSearches((prev) => prev.filter((s) => s.id !== id));
        toast({ title: "Saved search deleted" });
      } catch (err) {
        toast({
          title: "Delete failed",
          description: err instanceof Error ? err.message : "Unknown error",
          variant: "destructive",
        });
      }
    },
    [toast],
  );

  useEffect(() => {
    void loadSetup();
  }, [loadSetup]);

  useEffect(() => {
    setParameters((prev) => normalizePoisonedDataParameters(selectedMethodSlug, prev));
    setRows([]);
    setSelectedIds(new Set());
    setBatchId(null);
    setMatchCount(0);
    setOffset(0);
  }, [selectedMethodSlug]);

  const runPreview = useCallback(
    async (nextOffset = 0, nextParameters = parameters) => {
      try {
        setIsLoading(true);
        const normalized = normalizePoisonedDataParameters(selectedMethodSlug, nextParameters);
        setParameters(normalized);
        const response = await previewPoisonedData({
          methodSlug: selectedMethodSlug,
          targetTable: normalized.targetTable,
          parameters: normalized,
          limit: PAGE_SIZE,
          offset: nextOffset,
        });
        setRows(response.rows ?? []);
        setBatchId(response.batch?.id ?? null);
        setMatchCount(response.count ?? 0);
        setOffset(nextOffset);
        setSelectedIds(new Set());
      } catch (err) {
        toast({
          title: "Preview failed",
          description: err instanceof Error ? err.message : "Unable to preview records",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [parameters, selectedMethodSlug, toast],
  );

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) return new Set();
      return new Set([...prev, ...rows.map((row) => row.targetId)]);
    });
  };

  const handleQuarantine = async () => {
    if (!batchId || selectedIds.size === 0) return;
    try {
      setIsMutating(true);
      const result = await quarantinePoisonedData({
        batchId,
        targetTable,
        targetIds: [...selectedIds],
      });
      toast({ title: "Rows quarantined", description: `${result.quarantined} rows added to quarantine.` });
    } catch (err) {
      toast({
        title: "Quarantine failed",
        description: err instanceof Error ? err.message : "Unable to quarantine rows",
        variant: "destructive",
      });
    } finally {
      setIsMutating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteMode || !batchId) return;
    try {
      setIsMutating(true);
      const result =
        deleteMode === "all"
          ? await deleteCurrentPoisonedDataQuery({ batchId })
          : await deleteSelectedPoisonedData({
              batchId,
              methodSlug: selectedMethodSlug,
              targetTable,
              targetIds: [...selectedIds],
            });
      toast({ title: "Rows deleted", description: `${result.deleted} rows deleted.` });
      setDeleteMode(null);
      setConfirmText("");
      await runPreview(offset);
    } catch (err) {
      toast({
        title: "Delete failed",
        description: err instanceof Error ? err.message : "Unable to delete rows",
        variant: "destructive",
      });
    } finally {
      setIsMutating(false);
    }
  };

  const handleAiSuggest = async (refine = false) => {
    try {
      setIsLoading(true);
      const result = await suggestPoisonedDataFilter({
        parameters,
        operatorGoal: aiGoal,
        previewRows: rows,
        model: currentModel,
        refine,
      });
      setAiSuggestion(result.suggestion);
      const suggestionParams = (result.suggestion.parameters ?? {}) as PoisonedDataParameters;
      if (Object.keys(suggestionParams).length > 0) {
        setParameters((prev) => normalizePoisonedDataParameters("custom_filter_builder", { ...prev, ...suggestionParams }));
        setSelectedMethodSlug("custom_filter_builder");
      }
      toast({ title: "AI suggestion ready", description: `Suggested by ${result.model}` });
    } catch (err) {
      toast({
        title: "AI suggestion failed",
        description: err instanceof Error ? err.message : "Unable to call OpenRouter",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const parsed = settings.map((setting) => ({
        key: setting.key,
        description: setting.description,
        value: JSON.parse(settingsDraft[setting.key] ?? "{}"),
      }));
      const nextSettings = await updatePoisonedDataSettings(parsed);
      setSettings(nextSettings);
      setSettingsDraft(
        Object.fromEntries(nextSettings.map((setting) => [setting.key, JSON.stringify(setting.value, null, 2)])),
      );
      toast({ title: "Settings saved" });
    } catch (err) {
      toast({
        title: "Settings save failed",
        description: err instanceof Error ? err.message : "Check that every settings value is valid JSON.",
        variant: "destructive",
      });
    }
  };

  const pages = useMemo(() => Math.max(1, Math.ceil(matchCount / PAGE_SIZE)), [matchCount]);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const modelOptions = normalizeModelOptions(models, currentModel);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Poisoned Data</h1>
          <p className="max-w-3xl text-muted-foreground">
            Find, review, quarantine, and remove corrupted scrape records before they skew
            valuations, exports, and analysis.
          </p>
        </div>
        <Badge variant="outline" className="w-fit gap-2">
          <ShieldAlert className="h-3.5 w-3.5" />
          Human approval required
        </Badge>
      </div>

      {!canManageJobs && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Viewers can preview matches, but quarantine, delete, and settings changes require
            operator or admin access.
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="cleanup" className="space-y-4">
        <TabsList>
          <TabsTrigger value="cleanup">Cleanup workspace</TabsTrigger>
          <TabsTrigger value="jev">Jev checks</TabsTrigger>
          <TabsTrigger value="settings">AI settings</TabsTrigger>
        </TabsList>

        <TabsContent value="cleanup" className="space-y-4">
          {savedSearches.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Saved searches</CardTitle>
                <CardDescription>
                  Reusable monitoring queries. Click <strong>Load</strong> to apply parameters, then <strong>Preview</strong> to re-run.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {savedSearches.map((saved) => (
                    <div
                      key={saved.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{saved.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {saved.target_table} · {saved.method_slug}
                          </span>
                          {saved.last_match_count !== null && (
                            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-700">
                              {saved.last_match_count.toLocaleString()} last
                            </span>
                          )}
                        </div>
                        {saved.description && (
                          <p className="mt-1 text-xs text-muted-foreground">{saved.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button type="button" variant="outline" size="sm" onClick={() => handleLoadSavedSearch(saved)}>
                          Load
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={!canManageJobs}
                          onClick={() => handleDeleteSavedSearch(saved.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          <div className="grid gap-4 xl:grid-cols-[360px_1fr]">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Search setup
                </CardTitle>
                <CardDescription>Choose a table and detection method, then preview matches.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Data type</Label>
                  <Select
                    value={targetTable}
                    onValueChange={(value) =>
                      setParameters((prev) => ({
                        ...prev,
                        targetTable: value as PoisonedDataTargetTable,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {POISONED_DATA_TARGETS.map((target) => (
                        <SelectItem key={target.value} value={target.value}>
                          {target.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Detection method</Label>
                  <Select
                    value={selectedMethodSlug}
                    onValueChange={(slug) => {
                      setSelectedMethodSlug(slug);
                      setParameters(normalizePoisonedDataParameters(slug));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {methods.map((method) => (
                        <SelectItem key={method.slug} value={method.slug}>
                          {method.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedMethod && (
                    <div className="space-y-2 rounded-md border bg-muted/40 p-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{categoryLabel(selectedMethod.category)}</Badge>
                        <Badge variant={selectedMethod.risk_level === "high" ? "destructive" : "outline"}>
                          {selectedMethod.risk_level} risk
                        </Badge>
                      </div>
                      <p className="text-muted-foreground">{selectedMethod.description}</p>
                    </div>
                  )}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <KnobInput
                    label="Adapter"
                    value={parameters.adapterSlug ?? ""}
                    placeholder="domain-com-au"
                    onChange={(adapterSlug) => setParameters((prev) => ({ ...prev, adapterSlug }))}
                  />
                  <KnobInput
                    label="Postcode"
                    value={parameters.postcode ?? ""}
                    placeholder="3204"
                    onChange={(postcode) => setParameters((prev) => ({ ...prev, postcode }))}
                  />
                  <KnobInput
                    label="Suburb"
                    value={parameters.suburb ?? ""}
                    placeholder="Bentleigh"
                    onChange={(suburb) => setParameters((prev) => ({ ...prev, suburb }))}
                  />
                  <KnobInput
                    label="Text contains"
                    value={parameters.textContains ?? ""}
                    placeholder="Exampletown"
                    onChange={(textContains) => setParameters((prev) => ({ ...prev, textContains }))}
                  />
                </div>

                {KNOB_METHODS.has(selectedMethodSlug) && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <KnobInput
                      label={selectedMethodSlug === "implausible_sale_price" ? "Price below" : "Min price"}
                      type="number"
                      value={parameters.minPrice?.toString() ?? ""}
                      onChange={(minPrice) => setParameters((prev) => ({ ...prev, minPrice: Number(minPrice) || null }))}
                    />
                    {selectedMethodSlug !== "implausible_sale_price" && (
                      <KnobInput
                        label="Max price"
                        type="number"
                        value={parameters.maxPrice?.toString() ?? ""}
                        onChange={(maxPrice) => setParameters((prev) => ({ ...prev, maxPrice: Number(maxPrice) || null }))}
                      />
                    )}
                  </div>
                )}

                {selectedMethodSlug === "custom_filter_builder" && (
                  <div className="space-y-2">
                    <Label>Raw WHERE clause (PostgREST OR syntax)</Label>
                    <Textarea
                      value={parameters.whereClause ?? ""}
                      onChange={(event) => setParameters((prev) => ({ ...prev, whereClause: event.target.value }))}
                      placeholder="raw_data->>address.eq.null,sale_price.eq.0"
                      rows={3}
                      className="font-mono text-xs"
                    />
                    <p className="text-xs text-muted-foreground">
                      Optional. Appended to the structured filters above via PostgREST{" "}
                      <code className="rounded bg-muted px-1">.or()</code>. Server rejects{" "}
                      <code className="rounded bg-muted px-1">;</code>,{" "}
                      <code className="rounded bg-muted px-1">--</code>, DROP/ALTER/DELETE/UNION/etc.
                      See <code>docs/poisoned-data-raw-filter-and-saved-searches.md</code>.
                    </p>
                  </div>
                )}

                {selectedMethod?.category === "ai_assisted" && (
                  <div className="space-y-2">
                    <Label>AI goal</Label>
                    <Textarea value={aiGoal} onChange={(event) => setAiGoal(event.target.value)} rows={4} />
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="secondary" onClick={() => handleAiSuggest(false)} disabled={isLoading}>
                        <Bot className="mr-2 h-4 w-4" />
                        Ask AI
                      </Button>
                      <Button type="button" variant="outline" onClick={() => handleAiSuggest(true)} disabled={isLoading || rows.length === 0}>
                        Refine from preview
                      </Button>
                    </div>
                    {aiSuggestion && (
                      <pre className="max-h-48 overflow-auto rounded-md bg-muted p-3 text-xs">
                        {JSON.stringify(aiSuggestion, null, 2)}
                      </pre>
                    )}
                  </div>
                )}

                <Button type="button" className="w-full" onClick={() => runPreview(0)} disabled={isLoading}>
                  {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                  Preview matching records
                </Button>

                {saveDialogOpen ? (
                  <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                    <Label>Save name</Label>
                    <Input
                      value={saveDraftName}
                      onChange={(event) => setSaveDraftName(event.target.value)}
                      placeholder="Hunt #N — short description"
                    />
                    <Label>Description (optional)</Label>
                    <Textarea
                      value={saveDraftDescription}
                      onChange={(event) => setSaveDraftDescription(event.target.value)}
                      rows={2}
                      placeholder="What is this filter monitoring and why?"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleSaveCurrentSearch}
                        disabled={!saveDraftName.trim() || !canManageJobs}
                      >
                        Save
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setSaveDialogOpen(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setSaveDialogOpen(true)}
                    disabled={matchCount === 0 || !canManageJobs}
                  >
                    Save this search
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="space-y-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <CardTitle>Preview</CardTitle>
                    <CardDescription>
                      Showing {rows.length.toLocaleString()} of {matchCount.toLocaleString()} matches in{" "}
                      {getTargetLabel(targetTable)}.
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {showJev && (
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!canManageJobs || jev.isChecking}
                        title={`Paid model calls; at most ${JEV_BULK_MAX} per click`}
                        onClick={() => jev.check(jevBulkIds)}
                      >
                        {jev.isChecking ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Bot className="mr-2 h-4 w-4" />
                        )}
                        Check {selectedSnapshotIds.length > 0 ? "selected" : "visible"} with Jev (
                        {jevBulkIds.length}, max {JEV_BULK_MAX})
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={!canManageJobs || selectedIds.size === 0 || isMutating}
                      onClick={handleQuarantine}
                    >
                      Quarantine selected
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={!canManageJobs || selectedIds.size === 0 || isMutating}
                      onClick={() => setDeleteMode("selected")}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete selected
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!canManageJobs || !batchId || matchCount === 0 || isMutating}
                      onClick={() => setDeleteMode("all")}
                    >
                      Delete all matches
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {jev.checksError && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      Unable to load Jev checks:{" "}
                      {jev.checksError instanceof Error ? jev.checksError.message : "Unknown error"}
                    </AlertDescription>
                  </Alert>
                )}
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox checked={allVisibleSelected} onCheckedChange={toggleVisibleSelection} />
                        </TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Suburb</TableHead>
                        <TableHead>Adapter</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead>Date</TableHead>
                        {showJev && (
                          <TableHead>
                            Jev{" "}
                            {jev.isLoadingChecks && <Loader2 className="inline h-3 w-3 animate-spin" />}
                          </TableHead>
                        )}
                        <TableHead className="text-right">Raw</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={`${row.targetTable}-${row.targetId}`}>
                          <TableCell>
                            <Checkbox
                              checked={selectedIds.has(row.targetId)}
                              onCheckedChange={() => toggleSelection(row.targetId)}
                            />
                          </TableCell>
                          <TableCell className="min-w-56">
                            <div className="space-y-1">
                              <p className="text-sm font-medium">{row.reason}</p>
                              <Badge variant="outline">{Math.round(row.confidence * 100)}%</Badge>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-72 truncate">{formatValue(row.fields.address)}</TableCell>
                          <TableCell>
                            {formatValue(row.fields.suburb)} {formatValue(row.fields.postcode)}
                          </TableCell>
                          <TableCell>{formatValue(row.fields.adapter)}</TableCell>
                          <TableCell className="text-right">{formatValue(row.fields.price)}</TableCell>
                          <TableCell>{formatValue(row.fields.date)}</TableCell>
                          {showJev && (
                            <TableCell>
                              {row.targetTable === "property_snapshots" && (
                                <JevCheckCell
                                  check={jev.checks[row.targetId]}
                                  isChecking={jev.pending.has(row.targetId)}
                                  onCheck={() => jev.check([row.targetId])}
                                />
                              )}
                            </TableCell>
                          )}
                          <TableCell className="text-right">
                            <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedRow(row)}>
                              Inspect
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!isLoading && rows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                            No preview rows yet. Choose a method and run a preview.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {pages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={offset === 0 || isLoading}
                      onClick={() => runPreview(Math.max(0, offset - PAGE_SIZE))}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={offset + PAGE_SIZE >= matchCount || isLoading}
                      onClick={() => runPreview(offset + PAGE_SIZE)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="jev" className="space-y-4">
          <RecentJevChecks />
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                AI cleanup settings
              </CardTitle>
              <CardDescription>
                OpenRouter settings and prompts are stored server-side. The API key stays in edge
                function secrets.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Detected structured-output models</Label>
                <Select
                  value={currentModel}
                  onValueChange={(model) => {
                    const openrouter = getSettingValue(settings, "openrouter");
                    const next = { ...openrouter, model };
                    setSettingsDraft((prev) => ({ ...prev, openrouter: JSON.stringify(next, null, 2) }));
                    setSettings((prev) =>
                      prev.map((setting) => (setting.key === "openrouter" ? { ...setting, value: next } : setting)),
                    );
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {modelOptions.map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        {model.name ?? model.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {settings.map((setting) => (
                <div key={setting.key} className="space-y-2">
                  <Label>{setting.key}</Label>
                  {setting.description && <p className="text-xs text-muted-foreground">{setting.description}</p>}
                  <Textarea
                    value={settingsDraft[setting.key] ?? ""}
                    onChange={(event) => setSettingsDraft((prev) => ({ ...prev, [setting.key]: event.target.value }))}
                    rows={setting.key === "openrouter" ? 8 : 5}
                    className="font-mono text-xs"
                  />
                </div>
              ))}

              <Button type="button" onClick={handleSaveSettings} disabled={!canManageJobs}>
                Save settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedRow} onOpenChange={() => setSelectedRow(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Raw record</DialogTitle>
          </DialogHeader>
          <pre className="max-h-[70vh] overflow-auto rounded-md bg-muted p-4 text-xs">
            {JSON.stringify(selectedRow?.record ?? {}, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteMode} onOpenChange={(open) => !open && setDeleteMode(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete poisoned data?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteMode === "all"
                ? `This will re-run the saved server-side query and delete all ${matchCount.toLocaleString()} current matches. Type DELETE ${matchCount} to confirm.`
                : `This will permanently delete ${selectedIds.size.toLocaleString()} selected records and write an audit entry.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteMode === "all" && (
            <Input
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              placeholder={`DELETE ${matchCount}`}
            />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isMutating || (deleteMode === "all" && !canDeleteAll)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

function KnobInput({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input type={type} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

export default PoisonedData;
